import React, { useMemo, useState } from 'react';
import type { ColumnDescription } from 'react-bootstrap-table-next';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import StylishNewSelect from 'components/DesignSystems/New/StylishNewSelect';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import StylishNewInput from 'components/DesignSystems/New/StylishNewInput';
import { IncidentComs } from 'components/IAP/hooks/incidentComsHooks';
import { COMS_TYPES } from 'components/IAP/ICSConstants';

const cellInputClassName = 'form-control form-control-sm ics205-cell-input';

export type EditableComsFields = Pick<
  IncidentComs,
  | 'channel_number'
  | 'function'
  | 'channel_name'
  | 'rx_frequency'
  | 'rx_tone_nac'
  | 'tx_frequency'
  | 'tx_tone_nac'
  | 'mode'
  | 'remarks'
  | 'work_group_id'
  | 'cell_phone_number'
  | 'other'
> & {
  type?: string;
};

type IncidentComsTableProps = {
  rows: IncidentComs[];
  workGroups: { id: string; name: string }[];
  onCreate: (fields: EditableComsFields) => Promise<void>;
  onUpdate: (row: IncidentComs, fields: EditableComsFields) => Promise<void>;
  onArchive: (row: IncidentComs) => Promise<void>;
  isSaving?: boolean;
  readOnly?: boolean;
};

const defaultEditableValues: EditableComsFields = {
  type: 'Radio',
  channel_number: '',
  function: '',
  channel_name: '',
  work_group_id: '',
  rx_frequency: '',
  rx_tone_nac: '',
  tx_frequency: '',
  tx_tone_nac: '',
  mode: '',
  remarks: '',
  cell_phone_number: '',
  other: '',
};

const isNonradio = (type: string | undefined) =>
  (type ?? '').toLowerCase() === 'nonradio';

const IncidentComsTable: React.FC<IncidentComsTableProps> = ({
  rows,
  workGroups,
  onCreate,
  onUpdate,
  onArchive,
  isSaving = false,
  readOnly = false,
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [newRow, setNewRow] = useState<EditableComsFields>({
    ...defaultEditableValues,
  });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDrafts, setEditDrafts] = useState<
    Record<string, EditableComsFields>
  >({});
  const [searchTerm, setSearchTerm] = useState('');
  const [addRowError, setAddRowError] = useState<string | null>(null);

  const handleNewRowFieldChange = (
    field: keyof EditableComsFields,
    value: string
  ) => {
    setNewRow((prev) => ({ ...prev, [field]: value }));
    if (addRowError) setAddRowError(null);
  };

  const handleEditFieldChange = (
    id: string,
    field: keyof EditableComsFields,
    value: string
  ) => {
    setEditDrafts((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value,
      },
    }));
  };

  const startAddRow = () => {
    setNewRow({ ...defaultEditableValues });
    setAddRowError(null);
    setIsAdding(true);
  };

  const cancelAddRow = () => {
    setIsAdding(false);
    setNewRow({ ...defaultEditableValues });
    setAddRowError(null);
  };

  const requiredFieldsMessageRadio =
    'Channel name, RX frequency, and TX frequency are required for Radio.';
  const requiredFieldsMessageNonradio =
    'Channel name is required for Nonradio.';

  const handleCreate = async () => {
    const trimmed = {
      ...newRow,
      type: newRow.type ?? 'Radio',
    };

    if (isNonradio(trimmed.type)) {
      if (!trimmed.channel_name?.trim()) {
        setAddRowError(requiredFieldsMessageNonradio);
        return;
      }
    } else {
      if (
        !trimmed.channel_name?.trim() ||
        !trimmed.rx_frequency?.trim() ||
        !trimmed.tx_frequency?.trim()
      ) {
        setAddRowError(requiredFieldsMessageRadio);
        return;
      }
    }

    setAddRowError(null);
    await onCreate(trimmed);

    setIsAdding(false);
    setNewRow({ ...defaultEditableValues });
  };

  const startEditRow = (row: IncidentComs) => {
    setEditingId(row.id);
    setEditDrafts((prev) => ({
      ...prev,
      [row.id]: {
        type: row.type,
        channel_number: row.channel_number || '',
        function: row.function || '',
        channel_name: row.channel_name || '',
        work_group_id: row.work_group_id || '',
        rx_frequency: row.rx_frequency || '',
        rx_tone_nac: row.rx_tone_nac || '',
        tx_frequency: row.tx_frequency || '',
        tx_tone_nac: row.tx_tone_nac || '',
        mode: row.mode || '',
        remarks: row.remarks || '',
        cell_phone_number: row.cell_phone_number || '',
        other: row.other || '',
      },
    }));
  };

  const cancelEditRow = (id: string) => {
    if (editingId === id) {
      setEditingId(null);
    }
  };

  const handleSaveEdit = async (row: IncidentComs) => {
    const draft = editDrafts[row.id];
    if (!draft) {
      return;
    }
    await onUpdate(row, {
      ...draft,
      type: draft.type ?? row.type ?? 'Radio',
    });
    setEditingId(null);
  };

  const handleArchive = async (row: IncidentComs) => {
    await onArchive(row);
  };

  const filteredRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return rows;
    }
    return rows.filter((row) => {
      const haystack = [
        row.channel_number,
        row.function,
        row.channel_name,
        row.work_group_name,
        row.work_group_id,
        row.rx_frequency,
        row.tx_frequency,
        row.mode,
        row.remarks,
        row.cell_phone_number,
        row.other,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [rows, searchTerm]);

  const displayType = (type: string | undefined) =>
    (type ?? '').toLowerCase() === 'nonradio' ? 'Nonradio' : 'Radio';

  type TextCellOptions = {
    radioOnly?: boolean;
    nonradioOnly?: boolean;
    displayValue?: string;
    renderView?: (value: string) => React.ReactNode;
  };

  const renderEditableTextCell = (
    row: IncidentComs,
    field: keyof EditableComsFields,
    options?: TextCellOptions
  ): React.ReactNode => {
    const isEditing = editingId === row.id;
    const draft = editDrafts[row.id];
    const rowType = draft?.type ?? row.type;
    const hideAsRadio = options?.radioOnly && isNonradio(rowType);
    const hideAsNonradio = options?.nonradioOnly && !isNonradio(rowType);

    if (!isEditing || !draft) {
      if (hideAsRadio || hideAsNonradio) return '—';
      const value =
        options?.displayValue ?? (row[field] as string | undefined) ?? '';
      if (options?.renderView) return options.renderView(String(value));
      return value;
    }
    if (hideAsRadio || hideAsNonradio) return '—';
    return (
      <StylishNewInput
        className={cellInputClassName}
        value={(draft[field] as string) ?? ''}
        onChange={(e) => handleEditFieldChange(row.id, field, e.target.value)}
      />
    );
  };

  const workGroupOptions = useMemo(
    () =>
      workGroups.map((g) => ({
        label: g.name,
        value: g.id,
      })),
    [workGroups]
  );

  const renderEditableWorkGroupCell = (row: IncidentComs): React.ReactNode => {
    const isEditing = editingId === row.id;
    const draft = editDrafts[row.id];

    const selectedWorkGroupId = isEditing
      ? String(draft?.work_group_id || '')
      : String(row.work_group_id || '');

    const selectedOption = workGroupOptions.find(
      (o) => o.value === selectedWorkGroupId
    );

    if (!isEditing) {
      return row.work_group_name || selectedOption?.label || '—';
    }

    return (
      <StylishNewSelect
        options={workGroupOptions}
        value={selectedOption ?? null}
        onChange={(opt) => {
          if (opt && !Array.isArray(opt)) {
            handleEditFieldChange(row.id, 'work_group_id', String(opt.value));
          } else {
            handleEditFieldChange(row.id, 'work_group_id', '');
          }
        }}
        isClearable
        isSearchable
        classes="sml"
        menuPortalTarget={document.body}
      />
    );
  };

  const columns: ColumnDescription[] = [
    {
      dataField: 'type',
      text: 'Type',
      sort: true,
      formatter: (_cell: string, row: IncidentComs) => {
        return displayType(row.type);
      },
    },
    {
      dataField: 'channel_number',
      text: 'Channel #',
      sort: true,
      formatter: (_cell: string, row: IncidentComs) =>
        renderEditableTextCell(row, 'channel_number', { radioOnly: true }),
    },
    {
      dataField: 'function',
      text: 'Function',
      sort: true,
      formatter: (_cell: string, row: IncidentComs) =>
        renderEditableTextCell(row, 'function', { radioOnly: true }),
    },
    {
      dataField: 'channel_name',
      text: 'Channel Name / Talk Group',
      sort: true,
      formatter: (_cell: string, row: IncidentComs) =>
        renderEditableTextCell(row, 'channel_name'),
    },
    {
      dataField: 'work_group_id',
      text: 'Assignment (Work Group)',
      sort: true,
      formatter: (_cell: unknown, row: IncidentComs) =>
        renderEditableWorkGroupCell(row),
    },
    {
      dataField: 'rx_frequency',
      text: 'RX Freq N or W',
      sort: true,
      formatter: (_cell: string, row: IncidentComs) =>
        renderEditableTextCell(row, 'rx_frequency', { radioOnly: true }),
    },
    {
      dataField: 'rx_tone_nac',
      text: 'RX Tone/NAC',
      sort: false,
      formatter: (_cell: string, row: IncidentComs) =>
        renderEditableTextCell(row, 'rx_tone_nac', { radioOnly: true }),
    },
    {
      dataField: 'tx_frequency',
      text: 'TX Freq N or W',
      sort: true,
      formatter: (_cell: string, row: IncidentComs) =>
        renderEditableTextCell(row, 'tx_frequency', { radioOnly: true }),
    },
    {
      dataField: 'tx_tone_nac',
      text: 'TX Tone/NAC',
      sort: false,
      formatter: (_cell: string, row: IncidentComs) =>
        renderEditableTextCell(row, 'tx_tone_nac', { radioOnly: true }),
    },
    {
      dataField: 'mode',
      text: 'Mode (A, D, M)',
      sort: false,
      formatter: (_cell: string, row: IncidentComs) =>
        renderEditableTextCell(row, 'mode', { radioOnly: true }),
    },
    {
      dataField: 'remarks',
      text: 'Remarks',
      sort: false,
      formatter: (_cell: string, row: IncidentComs) =>
        renderEditableTextCell(row, 'remarks', {
          renderView: (value) => (
            <span
              title={value}
              className="text-truncate d-inline-block"
              style={{ maxWidth: 200 }}
            >
              {value}
            </span>
          ),
        }),
    },
    {
      dataField: 'cell_phone_number',
      text: 'Cell phone',
      sort: false,
      formatter: (_cell: string, row: IncidentComs) =>
        renderEditableTextCell(row, 'cell_phone_number', {
          nonradioOnly: true,
        }),
    },
    {
      dataField: 'other',
      text: 'Other',
      sort: false,
      formatter: (_cell: string, row: IncidentComs) =>
        renderEditableTextCell(row, 'other', { nonradioOnly: true }),
    },
    {
      dataField: 'actions',
      text: '',
      isDummyField: true,
      formatter: (_cell: unknown, row: IncidentComs) => {
        const isEditing = editingId === row.id;
        return (
          <div className="d-flex justify-content-end gap-2">
            {isEditing ? (
              <>
                <StylishNewButton
                  className="button button--icon"
                  type="button"
                  onClick={() => cancelEditRow(row.id)}
                  title="Cancel"
                >
                  <SharedIcon iconName="close" size="sm" />
                </StylishNewButton>
                <StylishNewButton
                  className="button button--icon"
                  type="button"
                  onClick={() => handleSaveEdit(row)}
                  title="Save"
                >
                  <SharedIcon iconName="check" size="sm" color="success" />
                </StylishNewButton>
              </>
            ) : (
              <>
                <StylishNewButton
                  className="button button--link button--sm"
                  type="button"
                  onClick={() => startEditRow(row)}
                >
                  <SharedIcon iconName="edit" />
                </StylishNewButton>
                <StylishNewButton
                  className="button button--link button--sm"
                  type="button"
                  onClick={() => handleArchive(row)}
                  title={row.archived ? 'Restore' : 'Archive'}
                >
                  <SharedIcon
                    iconName={row.archived ? 'unarchive' : 'archive'}
                  />
                </StylishNewButton>
              </>
            )}
          </div>
        );
      },
    },
  ];

  const columnHeaders = [
    'Type',
    'Channel Number',
    'Function',
    'Channel name / Trunked Radio System Task Group',
    'Assignment (Work Group)',
    'RX Freq N or W',
    'RX Tone/NAC',
    'TX Freq N or W',
    'TX Tone/NAC',
    'Mode (A, D, M)',
    'Remarks',
    'Cell phone',
    'Other',
    '',
  ];

  const displayColumns = readOnly ? columns.slice(0, -1) : columns;
  const displayColumnHeaders = readOnly
    ? columnHeaders.slice(0, -1)
    : columnHeaders;

  return (
    <div className="d-flex flex-column gap-3">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div className="d-flex align-items-center me-3 flex-grow-1 gap-2">
          <label className="form-label small text-muted mb-0">
            Basic Radio Channel Use:
          </label>
          <div className="input-group input-group-sm ics205-search-group flex-grow-1">
            <span className="input-group-text ics205-search-addon">
              <SharedIcon iconName="search" size="sm" />
            </span>
            <StylishNewInput
              type="text"
              className="form-control form-control-sm ics205-search-input"
              placeholder="Search channel..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        {!readOnly && (
          <StylishNewButton
            className="button button--primary button--sm"
            type="button"
            onClick={startAddRow}
          >
            <SharedIcon iconName="add" classes="me-2" />
            Add Channel
          </StylishNewButton>
        )}
      </div>

      <div className="table-responsive">
        <table className="table-sm mb-0 table">
          <thead>
            <tr>
              {displayColumnHeaders.map((text, idx) => (
                <th key={idx}>{text}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!readOnly && isAdding && addRowError && (
              <tr key="new-row-error">
                <td
                  colSpan={displayColumnHeaders.length}
                  className="bg-danger bg-opacity-25 text-danger small py-2"
                >
                  {addRowError}
                </td>
              </tr>
            )}
            {!readOnly && isAdding && (
              <tr key="new-row">
                <td>
                  <StylishNewSelect
                    options={COMS_TYPES}
                    value={
                      COMS_TYPES.find(
                        (o) => o.value === (newRow.type ?? 'Radio')
                      ) ?? COMS_TYPES[0]
                    }
                    onChange={(opt) => {
                      if (opt && !Array.isArray(opt)) {
                        handleNewRowFieldChange('type', String(opt.value));
                      }
                    }}
                    isClearable={false}
                    isSearchable={false}
                    classes="sml"
                    menuPortalTarget={document.body}
                  />
                </td>
                <td>
                  {isNonradio(newRow.type) ? (
                    <span className="text-muted">—</span>
                  ) : (
                    <StylishNewInput
                      className={cellInputClassName}
                      value={newRow.channel_number}
                      onChange={(e) =>
                        handleNewRowFieldChange(
                          'channel_number',
                          e.target.value
                        )
                      }
                    />
                  )}
                </td>
                <td>
                  {isNonradio(newRow.type) ? (
                    <span className="text-muted">—</span>
                  ) : (
                    <StylishNewInput
                      className={cellInputClassName}
                      value={newRow.function}
                      onChange={(e) =>
                        handleNewRowFieldChange('function', e.target.value)
                      }
                    />
                  )}
                </td>
                <td>
                  <StylishNewInput
                    className={cellInputClassName}
                    value={newRow.channel_name}
                    onChange={(e) =>
                      handleNewRowFieldChange('channel_name', e.target.value)
                    }
                    placeholder={
                      isNonradio(newRow.type) ? 'Contact name' : undefined
                    }
                  />
                </td>
                <td>
                  <StylishNewSelect
                    options={workGroupOptions}
                    value={
                      workGroupOptions.find(
                        (o) => o.value === (newRow.work_group_id ?? '')
                      ) ?? null
                    }
                    onChange={(opt) => {
                      if (opt && !Array.isArray(opt)) {
                        handleNewRowFieldChange(
                          'work_group_id',
                          String(opt.value)
                        );
                      } else {
                        handleNewRowFieldChange('work_group_id', '');
                      }
                    }}
                    isClearable
                    isSearchable
                    classes="sml"
                    menuPortalTarget={document.body}
                  />
                </td>
                <td>
                  {isNonradio(newRow.type) ? (
                    <span className="text-muted">—</span>
                  ) : (
                    <StylishNewInput
                      className={cellInputClassName}
                      value={newRow.rx_frequency}
                      onChange={(e) =>
                        handleNewRowFieldChange('rx_frequency', e.target.value)
                      }
                    />
                  )}
                </td>
                <td>
                  {isNonradio(newRow.type) ? (
                    <span className="text-muted">—</span>
                  ) : (
                    <StylishNewInput
                      className={cellInputClassName}
                      value={newRow.rx_tone_nac}
                      onChange={(e) =>
                        handleNewRowFieldChange('rx_tone_nac', e.target.value)
                      }
                    />
                  )}
                </td>
                <td>
                  {isNonradio(newRow.type) ? (
                    <span className="text-muted">—</span>
                  ) : (
                    <StylishNewInput
                      className={cellInputClassName}
                      value={newRow.tx_frequency}
                      onChange={(e) =>
                        handleNewRowFieldChange('tx_frequency', e.target.value)
                      }
                    />
                  )}
                </td>
                <td>
                  {isNonradio(newRow.type) ? (
                    <span className="text-muted">—</span>
                  ) : (
                    <StylishNewInput
                      className={cellInputClassName}
                      value={newRow.tx_tone_nac}
                      onChange={(e) =>
                        handleNewRowFieldChange('tx_tone_nac', e.target.value)
                      }
                    />
                  )}
                </td>
                <td>
                  {isNonradio(newRow.type) ? (
                    <span className="text-muted">—</span>
                  ) : (
                    <StylishNewInput
                      className={cellInputClassName}
                      value={newRow.mode}
                      onChange={(e) =>
                        handleNewRowFieldChange('mode', e.target.value)
                      }
                    />
                  )}
                </td>
                <td>
                  <StylishNewInput
                    className={cellInputClassName}
                    value={newRow.remarks}
                    onChange={(e) =>
                      handleNewRowFieldChange('remarks', e.target.value)
                    }
                  />
                </td>
                <td>
                  {isNonradio(newRow.type) ? (
                    <StylishNewInput
                      className={cellInputClassName}
                      value={newRow.cell_phone_number}
                      onChange={(e) =>
                        handleNewRowFieldChange(
                          'cell_phone_number',
                          e.target.value
                        )
                      }
                      placeholder="Cell"
                    />
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </td>
                <td>
                  {isNonradio(newRow.type) ? (
                    <StylishNewInput
                      className={cellInputClassName}
                      value={newRow.other}
                      onChange={(e) =>
                        handleNewRowFieldChange('other', e.target.value)
                      }
                      placeholder="Other"
                    />
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </td>
                <td className="text-end">
                  <div className="d-flex justify-content-end align-items-center gap-2">
                    <StylishNewButton
                      className="button button--icon"
                      type="button"
                      onClick={cancelAddRow}
                      title="Cancel"
                    >
                      <SharedIcon iconName="close" size="sm" />
                    </StylishNewButton>
                    <StylishNewButton
                      className="button button--icon"
                      type="button"
                      onClick={handleCreate}
                      disabled={isSaving}
                      title="Save"
                    >
                      <SharedIcon iconName="check" size="sm" color="success" />
                    </StylishNewButton>
                  </div>
                </td>
              </tr>
            )}
            {filteredRows.map((row) => (
              <tr
                key={row.id}
                className={row.archived ? 'opacity-75' : undefined}
              >
                {displayColumns.map((col, idx) => (
                  <td key={idx}>
                    {col.formatter
                      ? (
                          col.formatter as (
                            cell: unknown,
                            rowData: IncidentComs
                          ) => React.ReactNode
                        )(
                          (col as { dataField?: string }).dataField
                            ? row[
                                (col as { dataField: keyof IncidentComs })
                                  .dataField
                              ]
                            : undefined,
                          row
                        )
                      : null}
                  </td>
                ))}
              </tr>
            ))}
            {!isAdding && filteredRows.length === 0 && (
              <tr>
                <td
                  colSpan={displayColumnHeaders.length}
                  className="text-center"
                >
                  No communication channels configured
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {filteredRows.length > 0 && (
          <div className="small text-muted mt-1">
            {filteredRows.length}{' '}
            {filteredRows.length === 1 ? 'Channel' : 'Channels'}
          </div>
        )}
      </div>
    </div>
  );
};

export default IncidentComsTable;
